import { Box, Heading } from '@trmediaab/zebra';
import type { Order } from '@trmediaab/zebra-types/trhub';
import { useCallback, useState } from 'react';

import ErrorMessage from '../../components/ErrorMessage';
import Layout from '../../components/Layout';
import NextButton from '../../components/NextButton';
import VerificationCodeInput from '../../components/VerificationCodeInput';
import { setOrder, setRedeemCode } from '../../state';
import type { ErrorWithMessage } from '../../types/ErrorWithMessage';
import { ensureErrorWithMessage } from '../../utils/ensureErrorWithMessage';
import request from '../../utils/request';

const CODE_LENGTH = 7;

interface RedeemProps {
  onNext: () => void;
}

const Redeem = ({ onNext }: RedeemProps) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ErrorWithMessage | null>(null);
  const [enteredCode, setEnteredCode] = useState<string>();
  const isSubmittable =
    enteredCode != null && enteredCode.length === CODE_LENGTH;

  const onSubmit = useCallback(
    async (code: string) => {
      setBusy(true);
      try {
        const data = await request<Order>('gift/redeem', {
          method: 'POST',
          body: {
            giftCode: code,
          },
        });
        setRedeemCode(code);
        setOrder(data);
        onNext();
      } catch (error) {
        setError(ensureErrorWithMessage(error));
      } finally {
        setBusy(false);
      }
    },
    [onNext],
  );

  const onRedeemClick = useCallback(async () => {
    if (enteredCode != null) {
      setError(null);
      onSubmit(enteredCode);
    }
  }, [onSubmit, enteredCode]);

  const onCodeChange = useCallback(
    (code: string) => {
      setError(null);
      setEnteredCode(code);
      if (code.length === CODE_LENGTH) {
        onSubmit(code);
      }
    },
    [onSubmit],
  );

  return (
    <Layout
      areas={{ oneCol: ['header', 'main'] }}
      maxWidth="max-content"
      mx="auto"
    >
      <Layout.area area="header">
        <Heading.h2 variant="redeemCode" textAlign="center">
          Fyll i din kod här!
        </Heading.h2>
      </Layout.area>
      <Layout.area area="main">
        <VerificationCodeInput
          inputProps={{ disabled: true }}
          length={CODE_LENGTH}
          onChange={code => {
            onCodeChange(code);
          }}
        />
        {error != null && (
          <Box mt="3">
            <ErrorMessage error={error} />
          </Box>
        )}
      </Layout.area>
      <Layout.nextStepArea>
        <NextButton
          disabled={!isSubmittable || busy}
          label="Få din prenumeration!"
          showSpinner={false}
          onClick={onRedeemClick}
        />
      </Layout.nextStepArea>
    </Layout>
  );
};

export default Redeem;
